import { Asset, Button, Modal, PlusIcon } from '@clearcover/atlas';
import {
  AddressType,
  DriverType,
  VehicleType,
} from '@clearcover/core-app-types';
import React, { FC, useContext } from 'react';

import { Card, CardHeader, ExpansionPanel } from 'Shared/components';
import { useModalHashChange } from 'Shared/hooks';
import { combineStrings } from 'Shared/utils';
import styles from './styles.module.css';

import {
  addVehiclePermissionsPolicy,
  editVehiclePermissionsPolicy,
  removeVehiclePermissionsPolicy,
} from 'Admin/authPolicies';
import { PolicyContext } from 'Policies/utils';
import { ConflictingDraftModal } from '../ConflictingDraftModal';
import { PolicyVehicleDetails } from '../PolicyVehicleDetails';
import { EditVehicleButtons } from './EditVehicleButtons';
import { VehicleForm } from './VehicleForm';

export type PolicyVehiclesSectionType = {
  vehicles: Array<VehicleType>;
  drivers: Array<DriverType>;
  mailingAddress: AddressType;
  isNSA: boolean;
  showAntiTheftDevice: boolean;
};

export const PolicyVehiclesSection: FC<PolicyVehiclesSectionType> = ({
  vehicles,
  drivers,
  mailingAddress,
  isNSA,
  showAntiTheftDevice,
}) => {
  const {
    isEndorseablePolicy,
    resourceEffectiveGrants,
    canEndorseDuringRegionalEvents,
  } = useContext(PolicyContext);
  const { modalHashChange } = useModalHashChange('addVehicle');

  const canAddVehicle =
    canEndorseDuringRegionalEvents &&
    isEndorseablePolicy &&
    addVehiclePermissionsPolicy(resourceEffectiveGrants);

  const showEditVehicleButtons =
    canEndorseDuringRegionalEvents &&
    isEndorseablePolicy &&
    (removeVehiclePermissionsPolicy(resourceEffectiveGrants) ||
      editVehiclePermissionsPolicy(resourceEffectiveGrants));

  return (
    <Card id="vehicles-card">
      <CardHeader title={'Vehicles'}>
        {canAddVehicle && (
          <Modal
            onVisibilityChange={modalHashChange}
            variant="full"
            toggle={(openAddVehicleModal) => (
              <ConflictingDraftModal
                onApprove={openAddVehicleModal}
                renderToggle={(openModal) => (
                  <Button
                    className={styles.addVehicleButton}
                    size="sm"
                    variant="secondary"
                    onClick={openModal}
                    data-testid="open-add-vehicle-modal"
                  >
                    <Asset
                      icon={PlusIcon}
                      description="Add Component"
                      height={10}
                      width={10}
                      className={styles.icon}
                    />{' '}
                    Add
                  </Button>
                )}
              />
            )}
          >
            {(close) => (
              <VehicleForm
                type="create"
                close={close}
                drivers={drivers}
                mailingAddress={mailingAddress}
                isNSA={isNSA}
              />
            )}
          </Modal>
        )}
      </CardHeader>

      {vehicles.map((vehicle, index) => (
        <ExpansionPanel
          id={`vehicle-${index}`}
          key={`${vehicle.vin}-${index}`}
          head={
            <div className={styles.title} data-testid="vehicle-expansion-panel">
              <div className={styles.cardHeader}>
                <div>
                  <span>
                    {combineStrings([
                      vehicle.year,
                      vehicle.make,
                      vehicle.model,
                    ])}
                  </span>
                </div>
                <div>
                  {showEditVehicleButtons && (
                    <EditVehicleButtons
                      vehicle={vehicle}
                      drivers={drivers}
                      mailingAddress={mailingAddress}
                      withBackground={false}
                      disableRemoveButton={vehicles.length <= 1}
                      removeButtonTooltip={
                        vehicles.length <= 1
                          ? 'There must be at least one Insurable Item on the policy until the end of the term.'
                          : ''
                      }
                      isNSA={isNSA}
                    />
                  )}
                </div>
              </div>
            </div>
          }
        >
          <PolicyVehicleDetails
            vehicle={vehicle}
            drivers={drivers}
            showAntiTheftDevice={showAntiTheftDevice}
          />

          {showEditVehicleButtons && (
            <EditVehicleButtons
              vehicle={vehicle}
              drivers={drivers}
              mailingAddress={mailingAddress}
              disableRemoveButton={vehicles.length <= 1}
              removeButtonTooltip={
                vehicles.length <= 1
                  ? 'There must be at least one Insurable Item on the policy until the end of the term.'
                  : ''
              }
              isNSA={isNSA}
            />
          )}
        </ExpansionPanel>
      ))}
    </Card>
  );
};
